import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  isPickupLoanSelectable,
  type PickupAnalysisOpportunityAttempt,
  type PickupLoan
} from '../types';
import { pickupLoanBestPricing } from '../pickupAnalysisWorkspace';
import { PickupDriftInspectorSheet } from '../PickupDriftInspectorSheet';
import { PickupVerificationBadge } from '../PickupVerificationBadge';
import { pickupVerificationLabel } from '../pickupVerificationPresentation';
import type { PickupDriftInspectorData } from '../pickupDriftInspectorService';
import {
  buildPickupInvestorPricingRows,
  pickupInvestorPricingContext,
  pickupInvestorPricingInvestorCount
} from '../pickupInvestorPricing';
import {
  formatPickupInvestorDisplayName,
  formatPickupProductDisplayName
} from '../pickupInvestorDisplay';
import {
  pickupInvestorPricingGainDollars,
  pickupInvestorPricingStatusClass,
  pickupInvestorPricingStatusLabel
} from '../pickupInvestorPricingPresentation';
import { PickupInvestorRankChip } from '../PickupInvestorRankChip';

interface PickupDataTableProps {
  loans: PickupLoan[];
  opportunityAttempts?: PickupAnalysisOpportunityAttempt[];
  selectedIds: Set<string>;
  onSelectChange: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  onRefreshCache?: () => Promise<void> | void;
  refreshCacheUnavailableReason?: string;
  selectionDisabledReason?: string;
  checkingObLoanIds?: ReadonlySet<number>;
  nowMs?: number;
}

const EMPTY_OPPORTUNITY_ATTEMPTS: PickupAnalysisOpportunityAttempt[] = [];

interface SortHeaderProps {
  label: string;
  field: string;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  align?: 'left' | 'right';
  className?: string;
}

interface PickupExpiryDisplay {
  daysLeft: number | null;
  shortLabel: string;
  fullLabel: string;
}

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

function SortHeader({
  label,
  field,
  sortField,
  sortDir,
  onSort,
  align = 'left',
  className
}: SortHeaderProps) {
  const active = sortField === field;
  return (
    <th
      scope="col"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn(
        'h-8 border-b bg-muted/65 px-2 text-[9.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground',
        align === 'right' ? 'text-right' : 'text-left',
        className
      )}
    >
      <button
        type="button"
        className={cn(
          'inline-flex h-6 w-full items-center gap-1 whitespace-nowrap rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          align === 'right' ? 'justify-end' : 'justify-start'
        )}
        onClick={() => onSort(field)}
      >
        {label}
        {active && <span aria-hidden="true">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}

function PickupSquareCheckbox({
  checked,
  mixed = false,
  disabled = false,
  label,
  onChange
}: {
  checked: boolean;
  mixed?: boolean;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={mixed ? 'mixed' : checked}
      aria-label={label}
      disabled={disabled}
      data-pickup-square-checkbox
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
    >
      <span
        className={cn(
          'flex h-[14px] w-[14px] items-center justify-center rounded-[3px] border-[1.5px]',
          checked
            ? 'border-primary bg-primary text-primary-foreground'
            : disabled
              ? 'border-border bg-muted text-muted-foreground'
              : 'border-muted-foreground/60 bg-card text-transparent'
        )}
        aria-hidden="true"
      >
        {checked && <Check className="h-3 w-3" strokeWidth={3} />}
        {mixed && !checked && <span className="h-0.5 w-2 rounded-full bg-primary" />}
      </span>
    </button>
  );
}

function formatCurrency(amount: number | null): string {
  return amount === null ? '—' : CURRENCY_FORMATTER.format(amount);
}

function formatPrice(price: number | null): string {
  return price === null ? '—' : price.toFixed(3);
}

function formatSignedPoints(points: number | null): string {
  if (points === null) {
    return '—';
  }
  return `${points > 0 ? '+' : ''}${points.toFixed(3)}`;
}

function formatPickupExpiry(dateStr: string | null, nowMs = Date.now()): PickupExpiryDisplay {
  if (!dateStr) {
    return { daysLeft: null, shortLabel: '—', fullLabel: 'No lock expiration date' };
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!match) {
    return { daysLeft: null, shortLabel: '—', fullLabel: dateStr };
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const calendarDate = Date.UTC(year, month - 1, day);
  const now = new Date(nowMs);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const daysLeft = Math.round((calendarDate - today) / 86_400_000);
  const fullLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(new Date(calendarDate));

  return { daysLeft, shortLabel: `${daysLeft}d`, fullLabel };
}

function borrowerName(loan: PickupLoan): string {
  return [loan.borrower_first_name, loan.borrower_last_name].filter(Boolean).join(' ') || '—';
}

function verificationValue(
  loan: PickupLoan,
  liveInspection: PickupDriftInspectorData | null,
  checking: boolean
) {
  return {
    status: liveInspection?.verificationStatus ?? loan.pickup_verification_status,
    mismatchFields: liveInspection
      ? liveInspection.comparisons
        .filter(({ verdict }) => verdict === 'drift' || verdict === 'missing_in_ob')
        .map(({ field, tier }) => ({ field, tier }))
      : loan.pickup_verification_mismatch_fields,
    lastError: loan.pickup_verification_last_error,
    retryAfter: loan.pickup_verification_retry_after,
    lastVerifiedAt: liveInspection?.lastVerifiedAt ?? loan.pickup_last_verified_at,
    canonicalStateChanged: loan.pickup_canonical_state_changed,
    checking,
    lockDurationDays: loan.pickup_verified_lock_duration_days,
    obLockPeriodBucket: loan.pickup_verified_ob_lock_period_bucket
  };
}

function formatAnalyzedAt(value: string | null): string {
  if (!value) {
    return 'time unavailable';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(parsed);
}

export function PickupDataTable({
  loans,
  opportunityAttempts = [],
  selectedIds,
  onSelectChange,
  onSelectAll,
  sortField,
  sortDir,
  onSort,
  onRefreshCache,
  refreshCacheUnavailableReason,
  selectionDisabledReason,
  checkingObLoanIds = new Set<number>(),
  nowMs = Date.now()
}: PickupDataTableProps) {
  const [inspectionLoan, setInspectionLoan] = useState<PickupLoan | null>(null);
  const [inspectionTab, setInspectionTab] = useState<'drifts' | 'investor-pricing'>('drifts');
  const [expandedLoanIds, setExpandedLoanIds] = useState<Set<string>>(new Set());
  const [inspectionData, setInspectionData] = useState<Map<number, PickupDriftInspectorData>>(new Map());
  const inspectionReturnFocusRef = useRef<HTMLElement | null>(null);
  const inspectionObLoanId = inspectionLoan?.ob_loan_id ?? null;
  const attemptsByObLoanId = useMemo(() => {
    const result = new Map<number, PickupAnalysisOpportunityAttempt[]>();
    for (const attempt of opportunityAttempts) {
      const attempts = result.get(attempt.obLoanId) || [];
      attempts.push(attempt);
      result.set(attempt.obLoanId, attempts);
    }
    return result;
  }, [opportunityAttempts]);
  const inspectionTarget = useMemo(() => {
    if (!inspectionLoan) {
      return null;
    }
    const inspectionAttempts = inspectionLoan.ob_loan_id
      ? attemptsByObLoanId.get(inspectionLoan.ob_loan_id) || EMPTY_OPPORTUNITY_ATTEMPTS
      : EMPTY_OPPORTUNITY_ATTEMPTS;
    return {
      encompassLoanId: inspectionLoan.encompass_loan_id,
      obLoanId: inspectionLoan.ob_loan_id,
      loanNumber: inspectionLoan.trans_details_loan_number,
      borrowerName: borrowerName(inspectionLoan),
      pricing: pickupInvestorPricingContext(inspectionLoan, inspectionAttempts)
    };
  }, [attemptsByObLoanId, inspectionLoan]);
  const handleInspectionDataLoaded = useCallback((data: PickupDriftInspectorData) => {
    if (!inspectionObLoanId) {
      return;
    }
    setInspectionData((previous) => {
      const next = new Map(previous);
      next.set(inspectionObLoanId, data);
      return next;
    });
  }, [inspectionObLoanId]);
  const selectableLoans = selectionDisabledReason ? [] : loans.filter(isPickupLoanSelectable);
  const selectedOnPage = selectableLoans.filter((loan) => selectedIds.has(loan.encompass_loan_id)).length;
  const allSelected = selectableLoans.length > 0 && selectedOnPage === selectableLoans.length;
  const someSelected = selectedOnPage > 0 && !allSelected;

  const openInspector = (
    loan: PickupLoan,
    returnFocusElement: HTMLElement,
    tab: 'drifts' | 'investor-pricing' = 'drifts'
  ) => {
    inspectionReturnFocusRef.current = returnFocusElement;
    setInspectionTab(tab);
    setInspectionLoan(loan);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="overflow-x-auto" data-pickup-analysis-grid>
        <table className="w-full min-w-[1050px] table-fixed border-collapse font-sans text-[11.5px] leading-[1.2] text-foreground tabular-nums">
          <colgroup>
            <col className="w-[26px]" />
            <col className="w-[34px]" />
            <col className="w-[112px]" />
            <col className="w-[145px]" />
            <col className="w-[92px]" />
            <col className="w-[224px]" />
            <col className="w-[76px]" />
            <col className="w-[76px]" />
            <col className="w-[76px]" />
            <col className="w-[96px]" />
            <col className="w-[58px]" />
            <col className="w-[132px]" />
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr>
              <th scope="col" aria-label="Investor pricing" className="h-8 border-b bg-muted/65" />
              <th scope="col" className="h-8 border-b bg-muted/65 px-[5px] text-left">
                <PickupSquareCheckbox
                  checked={allSelected}
                  mixed={someSelected}
                  disabled={selectableLoans.length === 0}
                  label="Select all eligible loans on this page"
                  onChange={onSelectAll}
                />
              </th>
              <SortHeader label="Loan" field="trans_details_loan_number" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Borrower" field="borrower" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Amount" field="trans_details_loan_amt" sortField={sortField} sortDir={sortDir} onSort={onSort} align="right" />
              <SortHeader label="Investor route" field="investor" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Lock" field="locked_price" sortField={sortField} sortDir={sortDir} onSort={onSort} align="right" />
              <SortHeader label="Best" field="best_price" sortField={sortField} sortDir={sortDir} onSort={onSort} align="right" />
              <SortHeader label="Pickup" field="pickup_points" sortField={sortField} sortDir={sortDir} onSort={onSort} align="right" className="text-primary" />
              <SortHeader label="$" field="pickup_dollars" sortField={sortField} sortDir={sortDir} onSort={onSort} align="right" className="text-primary" />
              <SortHeader label="Exp" field="lock_expiration" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Verify" field="pickup_verification_status" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          {loans.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={12} className="h-24 border-b px-4 text-center text-muted-foreground">
                  No loans in this category. Refresh OB Cache to load current data.
                </td>
              </tr>
            </tbody>
          ) : loans.map((loan) => {
              const id = loan.encompass_loan_id;
              const pricing = pickupLoanBestPricing(loan);
              const amount = loan.base_loan_amount || loan.trans_details_loan_amt;
              const opportunityLoanAttempts = loan.ob_loan_id
                ? attemptsByObLoanId.get(loan.ob_loan_id) || []
                : [];
              const investorPricingRows = buildPickupInvestorPricingRows(
                pickupInvestorPricingContext(loan, opportunityLoanAttempts)
              );
              const hasInvestorPricing = investorPricingRows.length > 0;
              const investorCount = pickupInvestorPricingInvestorCount(investorPricingRows);
              const expanded = expandedLoanIds.has(id);
              const expansionId = `pickup-investor-pricing-${id}`;
              const expiration = formatPickupExpiry(
                loan.lock_expiration_date || loan.rate_lock_buy_side_lock_expires,
                nowMs
              );
              const recentlyAnalyzed = Boolean(
                loan.analyzed_at
                && nowMs - new Date(loan.analyzed_at).getTime() < 24 * 60 * 60 * 1000
              );
              const verificationBlocksSelection = !loan.pickup_canonical_state_changed
                && (
                  loan.pickup_verification_status === 'waiting_for_ob_sync'
                  || loan.pickup_verification_status === 'ob_mismatch'
                );
              const selectable = !selectionDisabledReason && isPickupLoanSelectable(loan);
              const selectionBlockedMessage = selectionDisabledReason
                || loan.needs_review_reason
                || pickupVerificationLabel(loan.pickup_verification_status);
              const liveInspection = loan.ob_loan_id ? inspectionData.get(loan.ob_loan_id) || null : null;
              const checkbox = (
                <PickupSquareCheckbox
                  checked={selectedIds.has(id)}
                  disabled={!selectable}
                  label={`Select ${loan.trans_details_loan_number || 'loan'}`}
                  onChange={(checked) => onSelectChange(id, checked)}
                />
              );

              return (
                <tbody key={id} data-pickup-loan-row-group={id}>
                <tr
                  data-pickup-analysis-row={loan.trans_details_loan_number || id}
                  data-pickup-inspected={inspectionLoan?.encompass_loan_id === id ? 'true' : 'false'}
                  tabIndex={0}
                  className={cn(
                    'group h-[30px] cursor-pointer border-l-[3px] border-l-transparent hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                    recentlyAnalyzed && 'bg-success/10',
                    selectedIds.has(id) && 'bg-success/5',
                    inspectionLoan?.encompass_loan_id === id && 'border-l-warning bg-warning/10'
                  )}
                  onClick={(event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest('button, a, input, [role="checkbox"]')) {
                      return;
                    }
                    openInspector(loan, event.currentTarget);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') {
                      return;
                    }
                    const target = event.target as HTMLElement;
                    if (target.closest('button, a, input, [role="checkbox"]')) {
                      return;
                    }
                    event.preventDefault();
                    openInspector(loan, event.currentTarget);
                  }}
                >
                  <td className="border-b px-0 py-0.5 text-center">
                    {hasInvestorPricing && (
                      <button
                        type="button"
                        aria-label={`${expanded ? 'Collapse' : 'Expand'} investor pricing for ${loan.trans_details_loan_number || 'loan'}`}
                        aria-expanded={expanded}
                        aria-controls={expansionId}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedLoanIds((previous) => {
                            const next = new Set(previous);
                            if (next.has(id)) {
                              next.delete(id);
                            } else {
                              next.add(id);
                            }
                            return next;
                          });
                        }}
                      >
                        {expanded
                          ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                          : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
                      </button>
                    )}
                  </td>
                  <td className="border-b px-[5px] py-0.5" onClick={(event) => event.stopPropagation()}>
                    {verificationBlocksSelection || !selectable ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex h-6 w-6 cursor-not-allowed items-center justify-center" tabIndex={0}>
                            {checkbox}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {selectionBlockedMessage}
                        </TooltipContent>
                      </Tooltip>
                    ) : checkbox}
                  </td>
                  <td className="border-b px-2 py-0.5 font-semibold text-foreground">
                    <span className="block min-w-0 truncate" title={loan.trans_details_loan_number || undefined}>
                      {loan.trans_details_loan_number || '—'}
                    </span>
                    {loan.needs_review_reason && (
                      <span
                        className="block truncate text-[9px] font-medium text-muted-foreground"
                        data-pickup-selection-reason
                        title={loan.needs_review_reason}
                      >
                        {loan.needs_review_reason}
                      </span>
                    )}
                  </td>
                  <td className="truncate border-b px-2 py-0.5" title={borrowerName(loan)}>{borrowerName(loan)}</td>
                  <td className="border-b px-2 py-0.5 text-right text-foreground">{formatCurrency(amount)}</td>
                  <td className="truncate border-b px-2 py-0.5" title={pricing.investorRoute || undefined}>
                    {pricing.investorRoute || '—'}
                  </td>
                  <td className="border-b px-2 py-0.5 text-right font-mono">{formatPrice(loan.locked_price)}</td>
                  <td className="border-b px-2 py-0.5 text-right font-mono font-bold text-primary">{formatPrice(pricing.bestPrice)}</td>
                  <td className={cn(
                    'border-b px-2 py-0.5 text-right font-bold',
                    pricing.pickupPoints !== null && pricing.pickupPoints > 0 && 'text-primary',
                    pricing.pickupPoints !== null && pricing.pickupPoints < 0 && 'text-destructive',
                    pricing.pickupPoints === null && 'text-muted-foreground'
                  )}>
                    {formatSignedPoints(pricing.pickupPoints)}
                  </td>
                  <td className={cn(
                    'border-b px-2 py-0.5 text-right',
                    pricing.pickupDollars !== null && pricing.pickupDollars > 0 && 'text-primary',
                    pricing.pickupDollars !== null && pricing.pickupDollars < 0 && 'text-destructive',
                    pricing.pickupDollars === null && 'text-muted-foreground'
                  )}>
                    {formatCurrency(pricing.pickupDollars)}
                  </td>
                  <td
                    className={cn(
                      'border-b px-2 py-0.5',
                      expiration.daysLeft !== null && expiration.daysLeft <= 3 && 'font-semibold text-destructive',
                      expiration.daysLeft !== null && expiration.daysLeft > 3 && expiration.daysLeft <= 7 && 'font-semibold text-foreground'
                    )}
                    title={expiration.fullLabel}
                  >
                    {expiration.shortLabel}
                  </td>
                  <td className="border-b px-2 py-0.5">
                    <PickupVerificationBadge
                      value={verificationValue(
                        loan,
                        liveInspection,
                        Boolean(loan.ob_loan_id && checkingObLoanIds.has(loan.ob_loan_id))
                      )}
                      target={{
                        encompassLoanId: loan.encompass_loan_id,
                        obLoanId: loan.ob_loan_id,
                        loanNumber: loan.trans_details_loan_number,
                        borrowerName: borrowerName(loan)
                      }}
                      onInspect={(returnFocusElement) => openInspector(loan, returnFocusElement)}
                      className="max-w-[126px]"
                    />
                  </td>
                </tr>
                {expanded && hasInvestorPricing && (
                  <tr id={expansionId} data-pickup-investor-pricing-row={loan.trans_details_loan_number || id}>
                    <td
                      colSpan={12}
                      className="border-b border-l-[3px] border-l-warning bg-muted/40 p-0"
                    >
                      <div className="px-6 py-3.5">
                        <div className="mb-2.5 flex flex-wrap items-baseline gap-3">
                          <span className="text-[9.5px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
                            Investor pricing
                          </span>
                          <span className="text-xs font-semibold text-foreground">
                            {investorCount} investor{investorCount === 1 ? '' : 's'} priced{' '}
                            {loan.trans_details_loan_number || 'this loan'} · repriced {formatAnalyzedAt(loan.analyzed_at)}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            className="ml-auto whitespace-nowrap"
                            onClick={(event) => {
                              event.stopPropagation();
                              openInspector(loan, event.currentTarget, 'investor-pricing');
                            }}
                          >
                            Open price paths ›
                          </Button>
                        </div>
                        <table className="w-full table-fixed border-collapse text-[11.5px] leading-none">
                          <colgroup>
                            <col className="w-[44px]" />
                            <col className="w-[19%]" />
                            <col className="w-[31%]" />
                            <col className="w-[76px]" />
                            <col className="w-[76px]" />
                            <col className="w-[92px]" />
                            <col className="w-[118px]" />
                          </colgroup>
                          <thead className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            <tr>
                              <th scope="col" className="px-[9px] py-1.5 text-left">Rank</th>
                              <th scope="col" className="px-[9px] py-1.5 text-left">Investor</th>
                              <th scope="col" className="px-[9px] py-1.5 text-left">Product</th>
                              <th scope="col" className="px-[9px] py-1.5 text-right">Price</th>
                              <th scope="col" className="px-[9px] py-1.5 text-right">Pickup pts</th>
                              <th scope="col" className="px-[9px] py-1.5 text-right">Gain $</th>
                              <th scope="col" className="px-[9px] py-1.5 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {investorPricingRows.map((row, index) => {
                              const investor = formatPickupInvestorDisplayName(row.investor) || row.investor;
                              const product = formatPickupProductDisplayName(row.productName, row.investor);
                              const gainDollars = pickupInvestorPricingGainDollars(row);
                              return (
                                <tr
                                  key={`${row.product.productId ?? 'product'}:${row.investor}:${index}`}
                                  data-pickup-investor-product={row.product.productId ?? index}
                                  className={cn('border-t', !row.beatsLock && 'text-muted-foreground')}
                                >
                                  <td className="px-[9px] py-1.5"><PickupInvestorRankChip row={row} /></td>
                                  <td
                                    className={cn(
                                      'truncate px-[9px] py-1.5',
                                      row.beatsLock ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
                                    )}
                                    title={investor}
                                  >
                                    {investor}
                                  </td>
                                  <td className="truncate px-[9px] py-1.5 text-muted-foreground" title={row.productName}>{product}</td>
                                  <td className="px-[9px] py-1.5 text-right font-mono tabular-nums">{formatPrice(row.price)}</td>
                                  <td className={cn(
                                    'px-[9px] py-1.5 text-right font-mono font-semibold tabular-nums',
                                    row.deltaPoints !== null && row.deltaPoints > 0 && 'text-success-strong',
                                    row.deltaPoints !== null && row.deltaPoints <= 0 && 'text-muted-foreground'
                                  )}>
                                    {formatSignedPoints(row.deltaPoints)}
                                  </td>
                                  <td className={cn(
                                    'px-[9px] py-1.5 text-right font-mono tabular-nums',
                                    gainDollars !== null ? 'text-success-strong' : 'text-muted-foreground'
                                  )}>
                                    {formatCurrency(gainDollars)}
                                  </td>
                                  <td className="px-[9px] py-1.5">
                                    <Badge
                                      variant="outline"
                                      className={cn('h-5 px-1.5 text-[9px]', pickupInvestorPricingStatusClass(row))}
                                    >
                                      {pickupInvestorPricingStatusLabel(row)}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
                          Latest run only, ranked by improvement over the {formatPrice(loan.locked_price)} locked price. Open price paths for the full run history.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
                </tbody>
              );
            })}
        </table>
      </div>

      <PickupDriftInspectorSheet
        target={inspectionTarget}
        open={Boolean(inspectionLoan)}
        initialTab={inspectionTab}
        returnFocusElement={inspectionReturnFocusRef.current}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setInspectionLoan(null);
          }
        }}
        onRefreshCache={onRefreshCache}
        refreshCacheUnavailableReason={refreshCacheUnavailableReason}
        onDataLoaded={handleInspectionDataLoaded}
        onOpenPipeline={inspectionLoan
          ? () => {
            const parameter = inspectionLoan.open_pickup_opportunity
              ? `opportunity=${encodeURIComponent(inspectionLoan.open_pickup_opportunity.opportunityId)}`
              : `loan=${encodeURIComponent(inspectionLoan.trans_details_loan_number || inspectionLoan.encompass_loan_id)}`;
            window.location.assign(`/internal/secondary/pickup-pipeline?${parameter}`);
          }
          : undefined}
      />
    </TooltipProvider>
  );
}
